/** Message thread (right pane) -- the same chat layout a user sees: inbound (user) bubbles left, outbound (AI / human) right. Loads only when a conversation is opened. Header carries the Participants button. */

import { useEffect, useRef } from 'react';
import { Button } from '@federa/components/primitives/Button';
import { spacing } from '@federa/components/css-refs.spacing';
import { typography } from '@federa/components/css-refs.typography';
import { borderRadius, borderWidth } from '@federa/components/css-refs.effects';
import { proportion } from '@federa/components/css-refs.layout';
import {
	FLEX_BETWEEN_STYLE,
	FLEX_CENTER_STYLE,
	FLEX_COLUMN_STYLE,
	FLEX_FILL_SCROLL_Y_STYLE,
	FLEX_FILL_TRUNCATE_STYLE,
} from '@federa/const/style-flex-presets';
import { VOICE_CALL_DIRECTIONS } from '@federa/const/connect-voice';
import { TEXT_TRUNCATE_STYLE } from '@federa/const/style-text-presets';
import { VIEWER_SHELL_COLORS, viewerVar } from './chats-viewer-tokens.ts';
import { conversationTitle, formatTime } from './format.ts';
import type { CSSProperties } from 'react';
import type { ChatViewerMessage } from '@federa/types/conversations';
import type { ThreadData } from '@federa/types/spa-chats-shapes';

interface Props {
	thread: ThreadData;
	onShowParticipants: () => void;
}

const main: CSSProperties = { ...FLEX_COLUMN_STYLE, ...FLEX_FILL_TRUNCATE_STYLE, background: viewerVar('--bg') };
const empty: CSSProperties = {
	flex: 1,
	...FLEX_CENTER_STYLE,
	color: viewerVar('--text-faint'),
	fontSize: typography.fontSize.sm,
};
const header: CSSProperties = {
	...FLEX_BETWEEN_STYLE,
	gap: spacing[3],
	padding: `${spacing[3.5]} ${spacing[5]}`,
	borderBottom: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
	background: viewerVar('--surface'),
};
const headTitle: CSSProperties = {
	fontSize: typography.fontSize.base,
	fontWeight: typography.fontWeight.bold,
	...TEXT_TRUNCATE_STYLE,
};
const headSub: CSSProperties = { fontSize: typography.fontSize.xs, color: viewerVar('--text-muted'), marginTop: spacing[0.25] };
/** The header-local delta over an `outline` `Button`: the viewer's own squared corner, hairline and label weight.
 *  `backgroundColor`, never the `background` shorthand -- `Button` repaints on hover by writing that longhand, and a
 *  shorthand beside it makes which one wins depend on CSSOM declaration order rather than on this file. */
const partsBtn: CSSProperties = {
	padding: `${spacing[2]} ${spacing[3.5]}`,
	borderRadius: viewerVar('--radius-sm'),
	border: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
	backgroundColor: viewerVar('--surface'),
	color: viewerVar('--text'),
	fontWeight: typography.fontWeight.semibold,
	fontSize: typography.hierarchy.tertiary.size,
	whiteSpace: 'nowrap',
	flexShrink: 0,
};
const list: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	...FLEX_FILL_SCROLL_Y_STYLE,
	padding: spacing[5],
	gap: spacing[2.5],
};
const state: CSSProperties = { padding: spacing[7], color: viewerVar('--text-muted'), fontSize: typography.hierarchy.tertiary.size };

/** Is this message OURS -- the side the bubble hangs on, tints and orders its meta row for? One reading of that
 *  question, so the bubble's colour and the row it sits in cannot come to disagree about which side a message is on. */
function isOutbound(message: ChatViewerMessage): boolean {
	return message.direction === VOICE_CALL_DIRECTIONS.OUTBOUND;
}

function bubbleWrap(outbound: boolean): CSSProperties {
	return { display: 'flex', flexDirection: 'column', alignItems: outbound ? 'flex-end' : 'flex-start' };
}
function bubble(message: ChatViewerMessage): CSSProperties {
	const outbound = isOutbound(message);
	const bg = !outbound ? viewerVar('--user-bubble') : message.actor === 'human' ? viewerVar('--human-bubble') : viewerVar('--ai-bubble');
	const color = outbound ? VIEWER_SHELL_COLORS.onAccent : viewerVar('--text');
	return {
		maxWidth: proportion.bubble,
		padding: `${spacing[2]} ${spacing[3]}`,
		borderRadius: borderRadius['2lg'],
		background: bg,
		color,
		fontSize: typography.fontSize.sm,
		lineHeight: 1.45,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		boxShadow: viewerVar('--shadow'),
	};
}
const meta: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: viewerVar('--text-faint'),
	margin: `${spacing[0.75]} ${spacing[1]} 0`,
	display: 'flex',
	gap: spacing[1.5],
};

function senderLabel(message: ChatViewerMessage): string {
	if (message.direction === VOICE_CALL_DIRECTIONS.INBOUND) return message.fromIdentifier ?? 'User';
	return message.actor === 'human' ? 'Agent' : 'Alex (AI)';
}

function Bubble({ message }: { message: ChatViewerMessage }) {
	const outbound = isOutbound(message);
	return (
		<div style={bubbleWrap(outbound)}>
			<div style={bubble(message)}>{message.content}</div>
			<div style={{ ...meta, flexDirection: outbound ? 'row-reverse' : 'row' }}>
				<strong style={{ fontWeight: typography.fontWeight.semibold }}>{senderLabel(message)}</strong>
				<span>{formatTime(message.createdAt)}</span>
			</div>
		</div>
	);
}

export function MessageThread({ thread, onShowParticipants }: Props) {
	const { conversation } = thread;
	const listRef = useRef<HTMLDivElement>(null);
	// Jump to the newest message (bottom) whenever a thread loads -- it renders oldest-first.
	useEffect(() => {
		const el = listRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [thread.messages]);
	if (!conversation) {
		return (
			<main style={main}>
				<div style={empty}>Select a conversation to read it.</div>
			</main>
		);
	}
	return (
		<main style={main}>
			<div style={header}>
				<div style={{ minWidth: 0 }}>
					<div style={headTitle}>{conversationTitle(conversation.friendlyName, conversation.id)}</div>
					<div style={headSub}>{thread.messages.length} messages</div>
				</div>
				<Button variant="outline" size="md" style={partsBtn} onClick={onShowParticipants}>
					Participants
				</Button>
			</div>
			{thread.error ? (
				<div style={state}>{thread.error}</div>
			) : thread.loading ? (
				<div style={state}>Loading messages...</div>
			) : thread.messages.length === 0 ? (
				<div style={state}>No messages in this conversation.</div>
			) : (
				<div ref={listRef} style={list}>
					{thread.messages.map((m) => (
						<Bubble key={m.id} message={m} />
					))}
				</div>
			)}
		</main>
	);
}
